import React, { useEffect, useRef, useState } from "react";
import "./Header.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaHeart, FaUser, FaShoppingCart } from "react-icons/fa";
import Book_appointment_modal from "../common-components/common-modals/book-appointment-modal/Book_appointment_modal";
import { useDispatch, useSelector } from "react-redux";
import { getUserInfo } from "../../features/user/userSlice";
import { getAllCart } from "../../features/cart/cartSlice";
import { GET } from "../../utils/api";
import { API } from "../../endpoints/common_endpoints";
import {
  getAllCategories,
  getAllGender,
  getAllSubCategory,
} from "../../features/master/masterSlice";

/* ==========================================================
   MENU FILTERS 
   ========================================================== */

const MENU_FILTERS_URL = API.GET_MENU_FILTERS;

const EMPTY_FACETS = {
  genders: [],
  shapes: [],
  brands: [],
  categories: [],
  materials: [],
  modalities: [],
};

const facetsCache = new Map();

const facetKey = ({ categoryId, genderId } = {}) =>
  `${categoryId || ""}|${genderId || ""}`;

// GET() returns the response body, and the backend wraps the payload in
// `data`, so the facets live at res.data. Accept a few shapes to be safe.
const unwrapFacets = (res) => {
  const candidates = [res, res?.data, res?.data?.data];
  return candidates.find((c) => c && Array.isArray(c.brands)) || null;
};

const loadFacets = (params = {}) => {
  const key = facetKey(params);

  if (facetsCache.has(key)) return facetsCache.get(key);

  const qs = new URLSearchParams();
  if (params.categoryId) qs.set("category_id", params.categoryId);
  if (params.genderId) qs.set("gender_id", params.genderId);

  const promise = Promise.resolve(GET(`${MENU_FILTERS_URL}?${qs.toString()}`))
    .then((res) => {
      const payload = unwrapFacets(res);

      if (!payload) {
        // request failed or unexpected shape: do not cache, retry next time
        console.warn("menu-filters: no usable data", res);
        facetsCache.delete(key);
        return EMPTY_FACETS;
      }

      return { ...EMPTY_FACETS, ...payload };
    })
    .catch((err) => {
      console.error("menu-filters failed:", err);
      facetsCache.delete(key);
      return EMPTY_FACETS;
    });

  facetsCache.set(key, promise);
  return promise;
};

// "Cat Eye" -> "cateye" (only used to pick an icon, not to filter data)
const shapeKeyOf = (name) =>
  (name || "").toLowerCase().replace(/[^a-z]/g, "");

const isExclusive = (b) =>
  !!(b?.exclusive || b?.is_exclusive || b?.exclusive_status || b?.isExclusive);


const isIntlBrand = (b) => {
  const v = b?.isinternational;
  if (v && v.type === "Buffer" && Array.isArray(v.data)) return v.data[0] === 1;
  return v === true || v === 1 || v === "1";
};

/* ==========================================================
   SCROLL HELPER (New Arrivals -> Home section)
   ========================================================== */

const scrollToSection = (id, tries = 0) => {
  const el = document.getElementById(id);

  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  // Home content may still be loading, so retry for ~3s
  if (tries < 30) {
    setTimeout(() => scrollToSection(id, tries + 1), 100);
  }
};

const JumpText = ({ text }) => (
  <span className="jump_wrap">
    {text.split("").map((char, i) => (
      <span
        key={i}
        className="jump_letter"
        style={{ animationDelay: `${i * 0.03}s` }}
      >
        {char === " " ? "\u00A0" : char}
      </span>
    ))}
  </span>
);

const ShapeIcon = ({ shapeKey }) => {
  const common = {
    width: 20,
    height: 14,
    viewBox: "0 0 24 16",
    fill: "none",
  };

  switch (shapeKey) {
    case "round":
      return (
        <svg {...common}>
          <circle cx="6" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
          <circle
            cx="18"
            cy="8"
            r="6"
            stroke="currentColor"
            strokeWidth="1.4"
          />
        </svg>
      );

    case "square":
      return (
        <svg {...common}>
          <rect
            x="1"
            y="2"
            width="10"
            height="10"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <rect
            x="13"
            y="2"
            width="10"
            height="10"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.4"
          />
        </svg>
      );

    case "rectangle":
      return (
        <svg {...common}>
          <rect
            x="0.5"
            y="3"
            width="11"
            height="7"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <rect
            x="12.5"
            y="3"
            width="11"
            height="7"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.4"
          />
        </svg>
      );

    case "cateye":
      return (
        <svg {...common}>
          <path
            d="M1 9c0-4 3-6 5-6s4 2 5 6"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
          <path
            d="M13 9c0-4 3-6 5-6s4 2 5 6"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
        </svg>
      );

    case "geometric":
      return (
        <svg {...common}>
          <polygon
            points="6,1 11,5 9,11 3,11 1,5"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
          <polygon
            points="18,1 23,5 21,11 15,11 13,5"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
        </svg>
      );

    case "oversized":
      return (
        <svg {...common}>
          <circle cx="6" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
          <circle
            cx="18"
            cy="8"
            r="7"
            stroke="currentColor"
            strokeWidth="1.4"
          />
        </svg>
      );

    case "wayfarer":
      return (
        <svg {...common}>
          <path
            d="M0.5 4h11l-1.5 6h-8z"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
          <path
            d="M12.5 4h11l-1.5 6h-8z"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
        </svg>
      );

    // any other shape coming from the database gets a generic glasses icon
    default:
      return (
        <svg {...common}>
          <path
            d="M6 2C3 2 1 5 1 8c0 3 2 5 5 5s5-2 5-5c0-1-0.5-2-1-3"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
          <path
            d="M18 2c-3 0-5 3-5 6 0 3 2 5 5 5s5-2 5-5c0-1-0.5-2-1-3"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
          />
        </svg>
      );
  }
};

const CART_FAN = {
  1: [{ x: 0, s: 0, r: 0 }],
  2: [
    { x: -15, s: 2, r: -8 },
    { x: 15, s: 2, r: 8 },
  ],
  3: [
    { x: -22, s: 4, r: -12 },
    { x: 0, s: 0, r: 0 },
    { x: 22, s: 4, r: 12 },
  ],
};

function Header({
  filters,
  handleFilterChange,
  handleMegaFilterChange,
  handleSingleCategoryFilter,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [modalShow, setModalShow] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [facets, setFacets] = useState({}); // cache-key -> facets
  const [openSection, setOpenSection] = useState(null); // which dropdown group is open on mobile

  const closeTimer = useRef(null);
  const headerRef = useRef(null);

  const is_signin = useSelector((state) => state.auth.is_signin);
  const user = useSelector((state) => state.user.userInfo);
  const cartItems = useSelector((state) => state.cart.allCarts);

  // Cart rows (badge count + images shown when hovering the cart link)
  const cartList = Array.isArray(cartItems?.data?.data)
    ? cartItems.data.data
    : Array.isArray(cartItems?.data)
      ? cartItems.data
      : Array.isArray(cartItems)
        ? cartItems
        : [];

  const cartCount = cartList.length;

  const cartImages = cartList
    .map((item) => item?.Product?.image)
    .filter(Boolean)
    .slice(0, 3)
    .map((img) => process.env.REACT_APP_IMAGE_URL + img);

  const categories = useSelector((state) => state.master.categories);
  const allGender = useSelector((state) => state.master.all_gender);
  const subcategories = useSelector((state) => state.master.subcategories);

  useEffect(() => {
    dispatch(getAllCategories());
    dispatch(getAllGender());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllCart(is_signin));

    if (is_signin) {
      dispatch(getUserInfo());
    }
  }, [is_signin, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);

    setMobileOpen(false);
    setMobileAccordion(null);
    setActiveMenu(null);
  }, [location.pathname]);

  // Scroll to a section after navigating (runs after the scroll-to-top above)
  useEffect(() => {
    const target = location.state?.scrollTo;

    if (target) {
      // small delay so Home's own scroll-to-top has finished first
      const t = setTimeout(() => scrollToSection(target), 150);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const setVar = () => {
      document.documentElement.style.setProperty(
        "--header-height",
        `${el.offsetHeight}px`,
      );
    };

    setVar();

    const observer = new ResizeObserver(setVar);
    observer.observe(el);

    window.addEventListener("resize", setVar);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, []);

  // collapse the dropdown groups whenever the mobile menu/accordion changes
  useEffect(() => {
    setOpenSection(null);
  }, [mobileAccordion, mobileOpen]);

  const openMenu = (key) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }

    setActiveMenu(key);
  };

  const scheduleClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }

    closeTimer.current = setTimeout(() => {
      setActiveMenu(null);
    }, 180);
  };

  const CATEGORY_IDS = {
    SPECTACLE_FRAMES: 5,
    SUNGLASSES: 2,
    SPECTACLE_LENSES: 1,
    CONTACT_LENSES: 3,
    META: 8,
    KIDS: 9,
    ACCESSORIES: 4,
  };

  const findCategoryById = (id) => categories?.find((c) => c.id === id);

  const sunglassesCat = findCategoryById(CATEGORY_IDS.SUNGLASSES);
  const eyeglassesCat = findCategoryById(CATEGORY_IDS.SPECTACLE_FRAMES);
  const kidsCat = findCategoryById(CATEGORY_IDS.KIDS);

  const kidsGender = allGender?.find((g) => g.name?.toLowerCase() === "kids");

  const contactLensCat = findCategoryById(CATEGORY_IDS.CONTACT_LENSES);
  const metaCategory = findCategoryById(CATEGORY_IDS.META);

  useEffect(() => {
    if (activeMenu === "sunglasses" && sunglassesCat) {
      dispatch(getAllSubCategory(sunglassesCat.id));
    }

    if (activeMenu === "eyeglasses" && eyeglassesCat) {
      dispatch(getAllSubCategory(eyeglassesCat.id));
    }
  }, [activeMenu, sunglassesCat, eyeglassesCat, dispatch]);

  const facetParamsFor = (menuKey) => {
    switch (menuKey) {
      case "sunglasses":
        return sunglassesCat ? { categoryId: sunglassesCat.id } : null;
      case "eyeglasses":
        return eyeglassesCat ? { categoryId: eyeglassesCat.id } : null;
      case "contact-lenses":
        return contactLensCat ? { categoryId: contactLensCat.id } : null;
      case "kids":
        return kidsGender ? { genderId: kidsGender.id } : null;
      case "brands":
        return {};
      default:
        return null;
    }
  };

  const facetsFor = (menuKey) => {
    const params = facetParamsFor(menuKey);
    return params ? facets[facetKey(params)] : undefined;
  };

  // load the data for whichever menu is open (desktop hover or mobile accordion)
  const openMenuKey = activeMenu || mobileAccordion;

  useEffect(() => {
    const params = facetParamsFor(openMenuKey);
    if (!params) return;

    const key = facetKey(params);
    let alive = true;

    loadFacets(params).then((data) => {
      if (alive) setFacets((prev) => ({ ...prev, [key]: data }));
    });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openMenuKey,
    sunglassesCat?.id,
    eyeglassesCat?.id,
    contactLensCat?.id,
    kidsGender?.id,
  ]);

  const closeAll = () => {
    setMobileOpen(false);
    setMobileAccordion(null);
    setActiveMenu(null);
  };

  const applyFilter = (filterType, value) => {
    handleFilterChange(filterType, value);
    navigate("/all-products");
    closeAll();
  };

  const applyMegaFilter = (
    primaryType,
    primaryValue,
    secondaryType,
    secondaryValue,
  ) => {
    handleMegaFilterChange(
      primaryType,
      primaryValue,
      secondaryType,
      secondaryValue,
    );
    navigate("/all-products");
    closeAll();
  };

  const applySingleCategory = (category) => {
    handleSingleCategoryFilter(category);
    navigate("/all-products");
    closeAll();
  };

  const goToNewArrivals = () => {
    closeAll();
    navigate("/", { state: { scrollTo: "new-arrivals" } });
  };

  // open/close one dropdown group inside the mobile submenu
  const toggleSection = (key) =>
    setOpenSection((prev) => (prev === key ? null : key));

  const renderFacetMenu = (o) => {
    const f = o.facets;

    if (!f) {
      return (
        <div className="mega_panel mega_panel_simple">
          <div className="mega_col">
            <ul>
              <li>
                <span className="muted">Loading…</span>
              </li>
            </ul>
          </div>
        </div>
      );
    }

    const brands = f.brands || [];
    const topBrands = brands.slice(0, 8);
    const exclusiveBrands = brands.filter(isExclusive).slice(0, 8);

    const cols = [];

    // one dropdown group: clickable heading + collapsible body (mobile only)
    const makeCol = (key, title, body) => (
      <div
        className={`mega_col ${openSection === key ? "sec_open" : ""}`}
        key={key}
      >
        <h6 className="mega_col_head" onClick={() => toggleSection(key)}>
          {title}
          <span className="mega_chev" />
        </h6>
        <div className="mega_col_body">{body}</div>
      </div>
    );

    if (o.onPickGender && f.genders?.length > 0) {
      cols.push(
        makeCol(
          "gender",
          "By Gender",
          <ul>
            {f.genders.map((g) => (
              <li key={g.id}>
                <span onClick={() => o.onPickGender(g)}>{g.name}</span>
              </li>
            ))}
          </ul>,
        ),
      );
    }

    if (!o.hideShapes && f.shapes?.length > 0) {
      cols.push(
        makeCol(
          "shape",
          "By Shape",
          <ul className="shape_list">
            {f.shapes.map((s) => (
              <li key={s.id}>
                <span onClick={() => o.onPickShape(s)}>
                  <ShapeIcon shapeKey={shapeKeyOf(s.name)} />
                  {s.name}
                </span>
              </li>
            ))}
          </ul>,
        ),
      );
    }

    if (topBrands.length > 0) {
      cols.push(
        makeCol(
          "top",
          o.brandsTitle || "Top Brands",
          <>
            <ul>
              {topBrands.map((b) => (
                <li key={b.id}>
                  <span onClick={() => o.onPickBrand(b)}>{b.name}</span>
                </li>
              ))}
            </ul>
            <span
              className="mega_viewall"
              onClick={() => {
                if (o.onViewAll) {
                  o.onViewAll();
                  return;
                }
                navigate("/all-products");
                closeAll();
              }}
            >
              View All Brands →
            </span>
          </>,
        ),
      );
    }

    if (!o.hideExclusive && exclusiveBrands.length > 0) {
      cols.push(
        makeCol(
          "exclusive",
          "Exclusive Brands",
          <>
            <ul>
              {exclusiveBrands.map((b) => (
                <li key={b.id}>
                  <span onClick={() => o.onPickBrand(b)}>{b.name}</span>
                </li>
              ))}
            </ul>
            <span
              className="mega_viewall"
              onClick={() => {
                navigate("/all-products");
                closeAll();
              }}
            >
              View All Exclusive →
            </span>
          </>,
        ),
      );
    }

    const extras = o.extras || (o.extra ? [o.extra] : []);

    extras.forEach((extra, idx) => {
      if (extra?.items?.length > 0) {
        cols.push(
          makeCol(
            `extra-${idx}`,
            extra.title,
            <ul>
              {extra.items.map((it) => (
                <li key={it.id}>
                  <span onClick={() => extra.onPick(it)}>{it.name}</span>
                </li>
              ))}
            </ul>,
          ),
        );
      }
    });

    if (cols.length === 0) {
      cols.push(
        <div className="mega_col" key="empty">
          <ul>
            <li>
              <span className="muted">Coming soon</span>
            </li>
          </ul>
        </div>,
      );
    }

    return (
      <div className="mega_panel" style={{ "--mega-cols": cols.length }}>
        {cols}

        <div className="mega_feature">
          {o.feature?.image ? (
            <img
              src={process.env.REACT_APP_IMAGE_URL + o.feature.image}
              alt={o.feature.alt}
            />
          ) : (
            <div className="mega_feature_placeholder" />
          )}

          <div className="mega_feature_caption">
            <p className="eyebrow subheading">New Season</p>

            <p className="title heading">{o.feature?.title}</p>

            <span className="mega_cta" onClick={o.feature?.onExplore}>
              Explore Now →
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryMegaMenu = (menuKey, category) =>
    renderFacetMenu({
      facets: facetsFor(menuKey),

      onPickGender: (g) =>
        category && applyMegaFilter("category", category, "gender", g),
      onPickShape: (s) =>
        category && applyMegaFilter("category", category, "frameShape", s),
      onPickBrand: (b) =>
        category && applyMegaFilter("category", category, "brands", b),

      extra: {
        title: category?.name || "Categories",
        items: subcategories || [],
        onPick: (sc) =>
          applyMegaFilter("category", { id: sc.id, name: sc.name }),
      },

      feature: {
        image: category?.image,
        alt: category?.name,
        title: category?.name || "Collection",
        onExplore: () => category && applyFilter("category", category),
      },
    });

  /*
   * CONTACT LENSES: Brand / Material / Modality
   */
  const renderContactLensMegaMenu = () => {
    const f = facetsFor("contact-lenses");

    const extras = [
      
      {
        title: "By Modality",
        items: f?.modalities || [],
        onPick: (m) =>
          contactLensCat &&
          applyMegaFilter("category", contactLensCat, "modality", m),
      },
    ];

    return renderFacetMenu({
      facets: f,

      // contact lenses have no gender / shape / exclusive columns
      hideShapes: true,
      hideExclusive: true,
      brandsTitle: "By Brand",

      onPickBrand: (b) =>
        contactLensCat &&
        applyMegaFilter("category", contactLensCat, "brands", b),
      onViewAll: () => contactLensCat && applySingleCategory(contactLensCat),

      extras,

      feature: {
        image: contactLensCat?.image,
        alt: contactLensCat?.name,
        title: contactLensCat?.name || "Contact Lenses",
        onExplore: () => contactLensCat && applySingleCategory(contactLensCat),
      },
    });
  };

  const renderKidsMegaMenu = () =>
    renderFacetMenu({
      facets: facetsFor("kids"),

      // no gender column: everything here is already the Kids gender
      onPickShape: (s) =>
        applyMegaFilter("gender", kidsGender, "frameShape", s),
      onPickBrand: (b) => applyMegaFilter("gender", kidsGender, "brands", b),

      // categories that actually have kids products
      extra: {
        title: "Categories",
        items: facetsFor("kids")?.categories || [],
        onPick: (c) => applyMegaFilter("gender", kidsGender, "category", c),
      },

      feature: {
        image: kidsCat?.image,
        alt: "Kids",
        title: "Kids Collection",
        onExplore: () => kidsCat && applyFilter("category", kidsCat),
      },
    });

  /*
   * BRANDS: Indian Brands / International Brands
   */
  const renderBrandsMegaMenu = () => {
    const f = facetsFor("brands");

    const sorted = f?.brands
      ? [...f.brands].sort((a, b) =>
          (a.name || "").localeCompare(b.name || ""),
        )
      : [];

    const indianBrands = sorted.filter((b) => !isIntlBrand(b));
    const internationalBrands = sorted.filter(isIntlBrand);

    const makeBrandCol = (key, title, list) => (
      <div
        className={`mega_col brand_group ${
          openSection === key ? "sec_open" : ""
        }`}
        key={key}
      >
        <h6 className="mega_col_head" onClick={() => toggleSection(key)}>
          {title}
          <span className="mega_chev" />
        </h6>

        <div className="mega_col_body">
          <ul className="brand_split_list">
            {list.map((b) => (
              <li key={b.id}>
                <span onClick={() => applyFilter("brands", b)}>{b.name}</span>
              </li>
            ))}

            {list.length === 0 && (
              <li>
                <span className="muted">
                  {f ? "Coming soon" : "Loading…"}
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>
    );

    return (
      <div className="mega_panel mega_panel_wide">
        {makeBrandCol("indian-brands", "Indian Brands", indianBrands)}
        {makeBrandCol(
          "international-brands",
          "International Brands",
          internationalBrands,
        )}
      </div>
    );
  };

  /*
   * NAV ITEMS
   */
  const NAV_ITEMS = [
    {
      key: "new-arrivals",
      label: "New Arrivals",
      onClick: goToNewArrivals,
    },

    {
      key: "meta",
      label: "Meta AI",
      badge: "NEW",
      onClick: () => applySingleCategory(metaCategory),
    },

    {
      key: "sunglasses",
      label: "Sunglasses",
      mega: () => renderCategoryMegaMenu("sunglasses", sunglassesCat),
      onDouble: () => applySingleCategory(sunglassesCat),
    },

    {
      key: "eyeglasses",
      label: "Spectacle Frames",
      mega: () => renderCategoryMegaMenu("eyeglasses", eyeglassesCat),
      onDouble: () => applySingleCategory(eyeglassesCat),
    },

    {
      key: "kids",
      label: "Kids",
      mega: renderKidsMegaMenu,
      onDouble: () =>
        kidsGender
          ? applyMegaFilter("gender", kidsGender)
          : applySingleCategory(kidsCat),
    },

    {
      key: "contact-lenses",
      label: "Contact Lenses",
      mega: renderContactLensMegaMenu,
      onDouble: () => applySingleCategory(contactLensCat),
    },

    {
      key: "Bapat Benefits",
      label: "Bapat Benefits",
      to: "/bapat-benefits",
      danger: true,
    },

    {
      key: "brands",
      label: "Brands",
      mega: renderBrandsMegaMenu,
      viewAllLabel: "View all products",
      onDouble: () => applySingleCategory(null),
    },

    {
      key: "about-us",
      label: "About Us",
      viewAllLabel: "Go to About Us page",
      onDouble: () => {
        navigate("/about-us");
        closeAll();
      },

      mega: () => (
        <div className="mega_panel mega_panel_simple">
          <div className="mega_col">
            <ul>
              <li>
                <Link to="/about-us" onClick={closeAll}>
                  About Us
                </Link>
              </li>

              <li>
                <span
                  onClick={() => {
                    setModalShow(true);
                    closeAll();
                  }}
                >
                  Appointments
                </span>
              </li>
            </ul>
          </div>
        </div>
      ),
    },

    {
      key: "quick-quiz",
      label: "Quick Quiz",
      to: "/quick-quiz",
    },
  ];

  const renderNavLink = (item) => {
    const isActive = item.to && location.pathname === item.to.split("?")[0];

    if (item.mega) {
      return (
        <span
          className={`nav_link heading ${
            activeMenu === item.key ? "active" : ""
          } ${item.danger ? "danger" : ""}`}
          onClick={() =>
            setActiveMenu(activeMenu === item.key ? null : item.key)
          }
          onDoubleClick={() => item.onDouble?.()}
        >
          <JumpText text={item.label} />

          {item.badge && (
            <span className="nav_badge heading">{item.badge}</span>
          )}
        </span>
      );
    }

    if (item.onClick) {
      return (
        <span
          className={`nav_link heading ${item.danger ? "danger" : ""}`}
          onClick={item.onClick}
        >
          <JumpText text={item.label} />

          {item.badge && (
            <span className="nav_badge heading">{item.badge}</span>
          )}
        </span>
      );
    }

    return (
      <Link
        to={item.to}
        className={`nav_link heading ${isActive ? "active" : ""} ${
          item.danger ? "danger" : ""
        }`}
        onClick={() => {
          setMobileOpen(false);
          setActiveMenu(null);
        }}
      >
        <JumpText text={item.label} />

        {item.badge && <span className="nav_badge heading">{item.badge}</span>}
      </Link>
    );
  };

  return (
    <>
      <section className="header_main" ref={headerRef}>
        <div className="header_blue_shell">
          <div className="header_top">
            <button
              className="mobile_toggle d-lg-none"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
            >
              <span />
              <span />
              <span />
            </button>

            {/* CENTER LOGO */}
            <Link
              to="/"
              className="brand_logo_link"
              onClick={() => setMobileOpen(false)}
            >
              <img
                className="head_logo"
                src={process.env.PUBLIC_URL + "/assets/images/logo/logo.png"}
                alt="logo"
              />
            </Link>

            <div className="profilesec">
              <Link to="/wishlist" className="proflink">
                <FaHeart className="proficn_svg" />
                <span className="profile_label">Wishlist</span>
              </Link>

              <Link
                to={is_signin ? "/my-account" : "/login"}
                className="proflink"
              >
                <FaUser className="proficn_svg" />
                <span className="profile_label">
                  {is_signin ? user?.name || "Account" : "Sign In"}
                </span>
              </Link>

              <Link to="/cart" className="proflink cart_link">
                <FaShoppingCart className="proficn_svg" />
                <span className="profile_label">Cart</span>
                <span className="cart_badge">{cartCount}</span>

                {cartImages.length > 0 && (
                  <span className="cart_pop" aria-hidden="true">
                    {cartImages.map((src, i) => {
                      const n = cartImages.length;
                      const p = CART_FAN[n][i];
                      const mid = (n - 1) / 2;

                      return (
                        <span
                          key={i}
                          className="cart_pop_img"
                          style={{
                            "--x": `${p.x}px`,
                            "--s": `${p.s}px`,
                            "--r": `${p.r}deg`,
                            "--d": `${i * 60}ms`,
                            zIndex: 10 - Math.round(Math.abs(i - mid) * 2),
                          }}
                        >
                          <img src={src} alt="" />
                        </span>
                      );
                    })}
                  </span>
                )}
              </Link>
            </div>
          </div>

          <nav className="header_nav d-none d-lg-block">
            <ul className="nav_list">
              {NAV_ITEMS.map((item) => (
                <li
                  key={item.key}
                  className="nav_item"
                  onMouseEnter={() => item.mega && openMenu(item.key)}
                  onMouseLeave={() => item.mega && scheduleClose()}
                >
                  {renderNavLink(item)}

                  {item.mega && (
                    <div
                      className={`mega_dropdown ${
                        item.key === "brands" ? "mega_dropdown_brands" : ""
                      } ${activeMenu === item.key ? "open" : ""}`}
                      onMouseEnter={() => {
                        if (closeTimer.current) {
                          clearTimeout(closeTimer.current);
                        }
                      }}
                      onMouseLeave={() => scheduleClose()}
                    >
                      {activeMenu === item.key && item.mega()}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className={`mobile_drawer ${mobileOpen ? "open" : ""}`}>
          <div className="mobile_drawer_header">
            <span className="mobile_drawer_title heading">Menu</span>

            <span onClick={() => setMobileOpen(false)} className="mobile_close">
              &times;
            </span>
          </div>

          <ul className="mobile_nav_list">
            {NAV_ITEMS.map((item) => (
              <li key={item.key}>
                <div
                  className="mobile_nav_row"
                  onClick={() => {
                    if (item.mega) {
                      setMobileAccordion(
                        mobileAccordion === item.key ? null : item.key,
                      );
                    } else if (item.onClick) {
                      item.onClick();
                      setMobileOpen(false);
                    } else {
                      navigate(item.to);
                      setMobileOpen(false);
                    }
                  }}
                  onDoubleClick={() => item.onDouble?.()}
                >
                  <span className={`heading ${item.danger ? "danger" : ""}`}>
                    {item.label}

                    {item.badge && (
                      <span className="nav_badge heading">{item.badge}</span>
                    )}
                  </span>

                  {item.mega && (
                    <span className="mobile_plus">
                      {mobileAccordion === item.key ? "−" : "›"}
                    </span>
                  )}
                </div>

                {item.mega && mobileAccordion === item.key && (
                  <div className="mobile_submenu">
                    {item.onDouble && (
                      <div
                        className="mobile_viewall"
                        onClick={() => item.onDouble()}
                      >
                        {item.viewAllLabel || `View all ${item.label}`} →
                      </div>
                    )}

                    {item.mega()}
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="mobile_drawer_footer">
            <span>Need Help?</span>
            <a href="tel:+9318001230000">Call: 1800-123-0000</a>
          </div>
        </div>

        {mobileOpen && (
          <div
            className="mobile_backdrop"
            onClick={() => setMobileOpen(false)}
          />
        )}

        <Book_appointment_modal
          show={modalShow}
          onHide={() => setModalShow(false)}
        />
      </section>

      <div className="header_spacer" />
    </>
  );
}

export default Header;